/**
 * Prompt Versions API service
 */

import apiClient, { ApiResponse } from './client';

export interface PromptVersion {
  version: number;
  status: 'live' | 'staging' | 'draft';
  labels: string[];
  created_at: string | null;
  updated_at: string | null;
  created_by_user_id: string | null;
  created_by: string | null;
  created_by_avatar_url: string | null;
  summary: string | null;
  full_summary: string | null;
}

export type PromptVersionStatus = PromptVersion['status'];

export interface VersionsListResponse {
  prompt_name: string | null;
  versions: PromptVersion[];
}

export interface TransitionRequest {
  version: number;
  action: 'deploy_to_staging' | 'publish_to_live';
}

export interface TransitionResponse {
  success: boolean;
  version: number;
  new_status: PromptVersionStatus;
  message: string;
}

// ── Diff types ──────────────────────────────────────────────────────────

export interface VersionDiffResponse {
  base_version: number;
  compare_version: number;
  base_status: PromptVersionStatus;
  compare_status: PromptVersionStatus;
  base_text: string;
  compare_text: string;
  is_latest: boolean;
  is_first_version: boolean;
}

export interface DeleteVersionResponse {
  success: boolean;
  version: number;
  message: string;
}

// ── Service ─────────────────────────────────────────────────────────────

const promptVersionsService = {
  listVersions: (orgId: string): Promise<ApiResponse<VersionsListResponse>> =>
    apiClient.get<VersionsListResponse>(`/api/prompt-versions/${orgId}/versions`),

  getVersionDiff: (orgId: string, version: number): Promise<ApiResponse<VersionDiffResponse>> =>
    apiClient.get<VersionDiffResponse>(`/api/prompt-versions/${orgId}/versions/${version}/diff`),

  getVersionDiffBetween: (orgId: string, base: number, compareTo: number): Promise<ApiResponse<VersionDiffResponse>> =>
    apiClient.get<VersionDiffResponse>(`/api/prompt-versions/${orgId}/versions/${base}/diff?compare_to=${compareTo}`),

  transitionVersion: (orgId: string, body: TransitionRequest): Promise<ApiResponse<TransitionResponse>> =>
    apiClient.post<TransitionResponse>(`/api/prompt-versions/${orgId}/versions/transition`, body),

  deleteVersion: (orgId: string, version: number): Promise<ApiResponse<DeleteVersionResponse>> =>
    apiClient.delete<DeleteVersionResponse>(`/api/prompt-versions/${orgId}/versions/${version}`),
};

export default promptVersionsService;
